import React, { type FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Text } from '@atlaskit/primitives';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import EditIcon from '@atlaskit/icon/core/edit';
import { FadeIn } from '@atlaskit/motion';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import type { ShareContentType } from '@confluence/share/entry-points/ShareButton';
import { usePendingRequestExists } from '@confluence/access-requests/entry-points/usePendingRequestExists';
import {
	useRequestToEditContent,
	type TriggerAnalyticsEventType,
} from '@confluence/no-permission/entry-points/useRequestToEditContent';

const i18n = defineMessages({
	askToEditContentLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit',
		description: 'The label for the button to request to edit content',
		defaultMessage: 'Ask to edit',
	},
	askToEditPendingLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.pending.label',
		description: 'The label element after the button which shows Pending request status',
		defaultMessage: 'Requested',
	},
	askToEditPendingRequestExistsFlagTitle: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.flag.title',
		description: 'Title for the flag which confirms an existing request was sent',
		defaultMessage: 'Request pending',
	},
	askToEditPendingRequestExistsFlagDescription: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.flag.description',
		description: 'Description for the flag which confirms an existing request was sent',
		defaultMessage: "You already sent a request. We'll send you an email when it's been approved.",
	},
});

export interface AskToEditMenuItemProps {
	contentType: ShareContentType;
	contentId: string;
	flags: FlagsStateContainer;
	triggerAnalyticsEvent: TriggerAnalyticsEventType;
	askToEditKey: string;
}

export const AskToEditMenuItem: FC<AskToEditMenuItemProps> = ({
	contentType,
	contentId,
	flags,
	triggerAnalyticsEvent,
	askToEditKey,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { requestToEditContent, hasRequestedEditPermission } = useRequestToEditContent({
		contentId,
		flags,
		triggerAnalyticsEvent,
	});

	const { pendingRequestExists, isLoadingForFirstTime: pendingRequestCheckLoading } =
		usePendingRequestExists({ contentId });

	const readyToSendRequest =
		!hasRequestedEditPermission && !pendingRequestExists && !pendingRequestCheckLoading;

	const handleItemClick = async () => {
		if (readyToSendRequest) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'requestAccessButton',
					source: 'moreActionsMenu',
					attributes: {
						contentType,
						accessType: 'edit',
						contentId,
						editDeniedButCanView: true,
					},
				},
			}).fire();
			await requestToEditContent();
		} else if (pendingRequestExists) {
			void flags.showInfoFlag({
				id: 'ask-to-edit.ellipsis.pending-edit-request-exists.flag',
				title: formatMessage(i18n.askToEditPendingRequestExistsFlagTitle),
				description: formatMessage(i18n.askToEditPendingRequestExistsFlagDescription),
				isAutoDismiss: true,
			});
		}
	};

	return (
		<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
			<DropdownItem
				key={askToEditKey}
				onClick={handleItemClick}
				elemBefore={<EditIcon label="" />}
				elemAfter={
					(hasRequestedEditPermission || pendingRequestExists) && (
						<FadeIn duration="medium">
							{(props) => (
								<div {...props}>
									<Text color="color.text.subtle">
										<FormattedMessage {...i18n.askToEditPendingLabel} />
									</Text>
								</div>
							)}
						</FadeIn>
					)
				}
			>
				<FormattedMessage {...i18n.askToEditContentLabel} />
			</DropdownItem>
		</ErrorBoundary>
	);
};
